import styles from "./FamiliesAdministrationFamily.module.scss";
import { useNavigate, useParams } from "react-router-dom";
import { useAddAdditionalLayoutButton } from "../../../../common/infrastructure/hooks/useAddAdditionalLayoutButton";
import { Icon, IconType } from "../../../../common/icons/Icon";
import {
  constructEditFamilyHref,
  constructPlannerEventHref,
  constructUserHref,
  routePaths,
} from "../../../../common/infrastructure/routes/routePaths";
import { useApiQuery } from "../../../../common/infrastructure/hooks/useApiQuery";
import { endpointsMetadata } from "../../../../common/infrastructure/routes/endpointsMetadata";
import { useState } from "react";
import { SpecificPageNotFound } from "../../../../common/components/errorPages/SpecificPageNotFound";
import { DefaultSection } from "../../../../common/components/common/DefaultSection";
import { SkeletonItem } from "../../../../common/components/skeletons/SkeletonItem";
import { IFamilyModel } from "./models/familyModel";
import { SectionInfoRow } from "../../../../common/components/common/SectionInfoRow";
import { dateUtility } from "../../../../common/infrastructure/utilities/dateUtility";
import { ItemsList } from "../../../../common/components/itemsList/ItemsList";
import { IFamilyEventHistoryModel } from "./models/familyEventHistoryModel";
import { classNames } from "primereact/utils";
import { humanizePaymentType } from "../../../../common/utilities/paymentUtility";
import { AppLink } from "../../../../common/components/button/link/Link";
import { Persona } from "../../../../common/components/common/Persona/Persona";
import { PlanRow } from "../../../../common/components/common/PlanRow/PlanRow";
import { getStatus } from "../../../planner/event/utils/PlannerEventStatusUtility";
import {
  HistoryItemKind,
  PaymentTypes,
  UserEventStatus,
} from "../../../../common/enums";
import {
  IfFalse,
  IfTrue,
  RenderWhen,
} from "../../../../common/infrastructure/components/RenderWhen";
import { AppDialog } from "../../../../common/components/AppDialog/AppDialog";
import { NotesButton } from "../../../../common/components/common/NotesDialog/NotesButton";
import { Tag } from "../../../../common/components/common/Tag/Tag";
import { PriceDisplay } from "../../../../common/components/PriceDisplay/PriceDisplay";
import { bg } from "date-fns/locale";
import { useAppContext } from "../../../../AppContext";
import { studioHidesPlans } from "../../../../common/infrastructure/helpers/studioHelper";

export const FamiliesAdministrationFamily = () => {
  const [showMembersPopup, setShowMembersPopup] = useState(false);
  const [showPlansPopup, setShowPlansPopup] = useState(false);
  const { id } = useParams();
  const { selectedStudio } = useAppContext();
  const showPlans = !studioHidesPlans(selectedStudio);

  const navigate = useNavigate();

  const { data: result, isLoading } = useApiQuery<IFamilyModel>({
    queryKey: [endpointsMetadata.family.getFamily, id],
    endpoint: `${endpointsMetadata.family.getFamily}?familyId=${id}`,
    enabled: id !== undefined,
    globalIsLoading: false,
  });

  useAddAdditionalLayoutButton({
    iconType: IconType.EditIcon,
    href: constructEditFamilyHref(Number(id)),
    rawPath: routePaths.familyEdit,
  });

  const backToFamilies = () => {
    navigate(routePaths.families);
  };

  const personaOnClick = (id?: number) => {
    if (!id) {
      return;
    }

    navigate(constructUserHref(id));
  };

  if (!result && !isLoading) {
    return (
      <SpecificPageNotFound
        text={"За съжаление няма такова семейство."}
        buttonText={"Обратно към списъка със семейства"}
        onButtonClick={backToFamilies}
      />
    );
  }

  return (
    <div className={styles.root}>
      <DefaultSection className={styles.root_title}>
        <div className={styles.root_header_title}>
          <SkeletonItem isLoading={isLoading} value={result?.name} />
        </div>
      </DefaultSection>

      <DefaultSection className={styles.root_info_section}>
        <SectionInfoRow
          label={"ID на семейството"}
          data={result?.id}
          isLoading={isLoading}
          skekletonWidth={100}
        />

        <SectionInfoRow
          label={"Добавено на"}
          data={dateUtility.formatDate(result?.dateAdded)}
          isLoading={isLoading}
          skekletonWidth={150}
        />

        <SectionInfoRow
          label={"Изтрито на"}
          data={dateUtility.formatDate(result?.dateDeleted)}
          isLoading={isLoading}
          skekletonWidth={170}
        />

        <SectionInfoRow
          label={"Потребители"}
          data={
            <AppLink
              className={styles.root_info_section_link}
              onClick={() => setShowMembersPopup(true)}
              label={"виж >"}
            />
          }
          isLoading={isLoading}
          skekletonWidth={120}
        />

        <RenderWhen condition={showPlans}>
          <SectionInfoRow
            label={"Планове"}
            data={
              <AppLink
                className={styles.root_info_section_link}
                onClick={() => setShowPlansPopup(true)}
                label={"виж >"}
              />
            }
            isLoading={isLoading}
            skekletonWidth={110}
          />
        </RenderWhen>
      </DefaultSection>

      <ItemsList
        isLoading={isLoading}
        noItemsText="Не беше открита история."
        className={styles.list}
        title="История"
        actionButton={
          <NotesButton
            entityId={result?.id}
            label={"Бележки"}
            getEndpoint={endpointsMetadata.family.getNotes}
            createEndpoint={endpointsMetadata.family.addNote}
            updateEndpoint={endpointsMetadata.family.updateNote}
            deleteEndpoint={endpointsMetadata.family.deleteNote}
            noResultsLabel={"Към момента няма добавени бележки за това семейство."}
          />
        }
        itemTemplate={(item, index, actionButtons, className) => {
          return (
            <FamilyHistoryItem
              className={className}
              family={item}
              key={item?.id ?? index}
              isLoading={isLoading}
              actionButtons={actionButtons}
            />
          );
        }}
        items={result?.history ?? []}
        fullHeight
      />

      <AppDialog
        header="Членове на семейството"
        tooltip="Членовете могат да бъдат редактирани от страницата за редактиране на семейството. <br/><br/> Отговорникът на семейството е маркиран със златна звезда."
        visible={showMembersPopup}
        className={styles.root_dialog}
        onHide={() => setShowMembersPopup(false)}
      >
        <div className={styles.personas_container}>
          <RenderWhen
            condition={!!result?.members && result?.members?.length > 0}
          >
            <IfTrue>
              {result?.members.map((member) => (
                <Persona
                  key={member.id}
                  firstName={member.firstName}
                  lastName={member.lastName}
                  id={member.id}
                  star={member.isHeadOfFamily}
                  onClick={() => personaOnClick(member.id)}
                />
              ))}
            </IfTrue>
            <IfFalse>Все още няма добавени членове.</IfFalse>
          </RenderWhen>
        </div>
      </AppDialog>

      <AppDialog
        header="История на плановете"
        visible={showPlansPopup}
        className={styles.root_dialog}
        onHide={() => setShowPlansPopup(false)}
      >
        <div className={styles.root_dialog_content_wrapper}>
          <RenderWhen condition={result?.plans.length === 0}>
            <IfTrue>
              <>Няма открити резултати.</>
            </IfTrue>
            <IfFalse>
              {result?.plans.map((plan) => (
                <PlanRow key={plan.id} plan={plan} />
              ))}
            </IfFalse>
          </RenderWhen>
        </div>
      </AppDialog>
    </div>
  );
};

interface IFamilyHistoryItemProps {
  className?: string;
  family?: IFamilyEventHistoryModel;
  isLoading: boolean;
  actionButtons?: JSX.Element | React.ReactNode;
}

const FamilyHistoryItem = ({
  className,
  family: event,
  isLoading,
  actionButtons,
}: IFamilyHistoryItemProps) => {
  const navigate = useNavigate();

  const isGymVisit = event?.kind === HistoryItemKind.GymVisit;

  const onEventClick = () => {
    if (!event || isGymVisit) {
      return;
    }
    const href = constructPlannerEventHref(event.id);
    navigate(href);
  };

  const { color: statusColor, label: statusLabel, additionalTag } = getStatus(
    event?.isEventPassed ?? false,
    event?.status,
    event?.isCancelled,
    event?.pricePaidInEur
  );

  const paymentTypeLabel = humanizePaymentType(
    event?.paymentType,
    event?.userPlanId,
    event?.planName
  );
  const visitsUserLabel =
    !!event?.initialPlanVisits && !!event?.visitsUsed
      ? `${event?.visitsUsed}/${event?.initialPlanVisits}`
      : "N/A";

  return (
    <div
      className={classNames(styles.item, {
        [styles.item_nonClickable]: isGymVisit,
      })}
      onClick={onEventClick}
    >
      <div className={styles.item_content}>
        <div className={styles.item_content_left}>
          <div className={styles.item_content_left_title}>
            <SkeletonItem
              isLoading={isLoading}
              value={event?.eventName}
              width={250}
            />
          </div>
          <SkeletonItem
            isLoading={isLoading}
            width={200}
            value={
              <div className={styles.item_content_left_usedBy}>
                {event?.usedBy}
              </div>
            }
          />
          <SkeletonItem
            isLoading={isLoading}
            width={200}
            value={
              <div className={styles.item_content_left_sub}>
                <Tag label={statusLabel} backgroundColor={statusColor} />
                <RenderWhen condition={!!additionalTag}>
                  <Tag label={additionalTag?.label ?? ""} backgroundColor={additionalTag?.color ?? ""} />
                </RenderWhen>
                <RenderWhen condition={!!paymentTypeLabel}>
                  <Tag label={paymentTypeLabel!} backgroundColor="#cedef9" />
                </RenderWhen>
              </div>
            }
          />
        </div>

        <div className={styles.item_content_right}>{actionButtons}</div>
      </div>

      <div className={styles.item_content_footer}>
        <SkeletonItem
          isLoading={isLoading}
          width={220}
          value={
            <div className={styles.item_content_footer_left}>
              <div>
                <span className={styles.item_content_footer_left_name}>
                  {isGymVisit ? "Влизане: " : "Дата: "}
                </span>
                <span className={styles.item_content_footer_left_value}>
                  {dateUtility.formatDate(
                    event?.date,
                    "eee, dd/MM/yyyy, HH:mm",
                    bg
                  )}
                </span>
              </div>
              <RenderWhen condition={isGymVisit && !!event?.checkedOutAt}>
                <div>
                  <span className={styles.item_content_footer_left_name}>
                    Излизане:{" "}
                  </span>
                  <span className={styles.item_content_footer_left_value}>
                    {dateUtility.formatDate(
                      event?.checkedOutAt,
                      "eee, dd/MM/yyyy, HH:mm",
                      bg
                    )}
                  </span>
                </div>
              </RenderWhen>
              <RenderWhen condition={isGymVisit && !event?.checkedOutAt}>
                <div>
                  <span className={styles.item_content_footer_left_name}>
                    Все още в залата
                  </span>
                </div>
              </RenderWhen>
            </div>
          }
        />
        <SkeletonItem
          width="20%"
          isLoading={isLoading}
          value={
            <div className={styles.item_content_footer_right}>
              <RenderWhen
                condition={
                  !!event?.pricePaid &&
                  (event.status === UserEventStatus.Arrived ||
                    event.status === UserEventStatus.OnlyPaid ||
                    event.status === UserEventStatus.Confirmed)
                }
              >
                <span className={styles.item_content_footer_right_pricePaid}>
                  <PriceDisplay
                    minPrice={event?.pricePaid ?? 0}
                    currencyId={event?.currencyId}
                  />
                </span>
              </RenderWhen>
              <span className={styles.item_content_footer_right_visits}>
                <RenderWhen
                  condition={event?.paymentType === PaymentTypes.Plan}
                >
                  | {visitsUserLabel}
                </RenderWhen>
              </span>
            </div>
          }
        />
      </div>
    </div>
  );
};
